import { IonIcon, IonInput } from "@ionic/react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type FormValidationError } from "@tanstack/react-form";
import { informationCircleOutline } from "ionicons/icons";
import { getErrorText } from "@/components/atoms/form-ion/utils";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { cx } from "@/lib/style/cva.config";

/**
 * Hoe lang je pauze was.
 *
 * Dit veld was een `<input type="time">`, en dat is het verkeerde instrument: een klok vraagt naar
 * een moment op de dag, niet naar een duur. Op Android krijg je er een wijzerplaat bij en op iOS
 * een wiel met 24 uren en 60 minuten -- 1440 standen om "een half uur" te zeggen, en op iOS ook nog
 * met een AM/PM-schakelaar die er niets mee te maken heeft. Vandaar het `data-force-24h` dat er
 * ooit tegenaan is geplakt.
 *
 * Bijna elke pauze is een van een handvol waarden, dus die staan er als knop. De rest vult het zelf
 * in, in minuten, want dat is de eenheid waarin mensen over pauze praten.
 *
 * Het zijn keuzerondjes en geen schakelaars: precies één is er waar, en de rest gaat vanzelf uit.
 * `aria-pressed` zou VoiceOver zes losse aan/uit-knoppen laten voorlezen, `role="radio"` leest "3
 * van 6". Dezelfde opbouw als de taalkiezer in de instellingen.
 *
 * De waarde blijft naar buiten toe `"HH:MM"`. Het formulier eromheen valideert en verstuurt die
 * vorm al, en die hoeft van een nieuw knopje niet te veranderen.
 */

/** In minuten. */
const PRESETS = [0, 15, 30, 45, 60];

/**
 * Vijf uur, als hint op het invoerveld -- niet als harde grens. Wat te lang is hangt van de dienst
 * af, en dat weet `breakTooLong` al; hier stilletjes afkappen terwijl iemand typt is verwarrender
 * dan het te laten staan.
 */
const MAX_MINUTES = 5 * 60;

export function toDurationString(minutes: number) {
  const safe = Math.max(0, Math.min(23 * 60 + 59, Math.round(minutes)));

  return `${Math.floor(safe / 60)
    .toString()
    .padStart(2, "0")}:${(safe % 60).toString().padStart(2, "0")}`;
}

export function toMinutes(duration: string) {
  const [hours, minutes] = duration.split(":").map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * Wat er in het formulier komt te staan als iemand `raw` in het minutenveld typt.
 *
 * Leeg is leeg en niet nul: één backspace maakte er "00:00" van, en die nul stond dan meteen weer
 * in beeld, zodat je hem eerst moest wegvegen voor je iets anders kon typen. De validatie loopt op
 * blur en op verzenden, dus een leeg veld geeft geen rode rand terwijl je nog bezig bent -- maar
 * een leeg veld dat stiekem "geen pauze" betekent zou dat wel doen.
 *
 * `null` betekent: hier valt niets van te maken, laat de vorige waarde staan.
 */
export function parseCustomMinutes(raw: string): string | null {
  if (raw.trim() === "") {
    return "";
  }

  const parsed = Number(raw);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return toDurationString(parsed);
}

type BreakField = {
  name: string;
  state: { value: string; meta: { errors: FormValidationError<unknown>[] } };
  handleChange: (value: string) => void;
  handleBlur: () => void;
};

export function BreakDurationField({
  field,
  showWarning = false,
}: {
  field: BreakField;
  showWarning?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const PreferredHaptics = usePreferredHaptics();
  const labelId = useId();
  const customInputRef = useRef<HTMLIonInputElement>(null);

  const minutes = toMinutes(field.state.value);
  const matchesPreset = minutes !== null && PRESETS.includes(minutes);

  // Een waarde die geen knop is, is er meestal al: een eerder ingediende pauze van 20 minuten
  // hoort het veld open te vinden, niet omgezet naar de dichtstbijzijnde knop.
  const [isCustom, setIsCustom] = useState(minutes !== null && !matchesPreset);

  // Wat er letterlijk in het invoerveld staat. Dat moet los van de waarde kunnen bestaan, anders
  // kun je het niet leegmaken om iets anders te typen: één backspace en "" wordt 0, waarna de nul
  // meteen weer in beeld staat en je hem opnieuw moet wegvegen.
  const [customText, setCustomText] = useState(
    minutes !== null && !matchesPreset ? String(minutes) : "",
  );

  const errorText = getErrorText({ language: i18n.language, errors: field.state.meta.errors });

  const select = (value: number) => {
    // Dezelfde tik als een tabknop of de avatar geeft: één knop indrukken is een losse handeling,
    // geen gebaar. (`selectionChanged` hoort binnen een start/end-paar en doet op Android niets
    // zolang dat paar er niet is -- daar is de kalender ooit op stukgelopen.)
    void PreferredHaptics.impact();
    setIsCustom(false);
    field.handleChange(toDurationString(value));
    field.handleBlur();
  };

  const openCustom = () => {
    void PreferredHaptics.impact();
    setCustomText(minutes === null ? "" : String(minutes));
    setIsCustom(true);
    // `setFocus` wacht zelf op de volgende frame, dus dit mag meteen na de state-wissel.
    void customInputRef.current?.setFocus();
  };

  const changeCustom = (raw: string) => {
    setCustomText(raw);

    const next = parseCustomMinutes(raw);

    if (next !== null) {
      field.handleChange(next);
    }
  };

  const optionClassName = (isSelected: boolean) =>
    cx(
      // 44 pixels hoog: onder die maat mist een duim de knop, en dat is precies de maat die
      // Apple als ondergrens hanteert.
      "inline-flex min-h-[44px] items-center rounded-full border border-solid px-4 text-sm font-medium transition-opacity active:opacity-70",
      isSelected
        ? "border-txt-primary bg-txt-primary text-txt-inverted"
        : "border-bordercolor-primary text-txt-secondary",
    );

  return (
    <div className="flex flex-col gap-2">
      <p id={labelId} className="text-sm font-medium text-txt-primary">
        {t("shift:hours.fields.breakDuration")}
      </p>

      <div role="radiogroup" aria-labelledby={labelId} className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            role="radio"
            aria-checked={!isCustom && minutes === preset}
            onClick={() => select(preset)}
            className={optionClassName(!isCustom && minutes === preset)}
          >
            {preset === 0
              ? t("shift:hours.break.none")
              : preset === 60
                ? t("shift:hours.break.hour")
                : t("shift:hours.break.minutes", { minutes: preset })}
          </button>
        ))}

        <button
          type="button"
          role="radio"
          aria-checked={isCustom}
          onClick={openCustom}
          className={optionClassName(isCustom)}
        >
          {t("shift:hours.break.custom")}
        </button>
      </div>

      {isCustom && (
        <IonInput
          ref={customInputRef}
          type="number"
          // Zonder dit krijg je op iOS het volledige toetsenbord in plaats van het cijferblok.
          inputmode="numeric"
          min={0}
          max={MAX_MINUTES}
          name={field.name}
          label={t("shift:hours.break.custom-label")}
          aria-label={t("shift:hours.break.custom-label")}
          labelPlacement="stacked"
          value={customText}
          onIonInput={(event) => changeCustom(String(event.detail.value ?? ""))}
          onIonBlur={field.handleBlur}
        />
      )}

      {!!errorText && <p className="text-xs font-medium text-error-120">{errorText}</p>}

      {showWarning && !errorText && (
        <div className="bg-warning-100/10 flex items-start gap-2 rounded-lg border border-solid border-warning-100 p-3">
          <IonIcon icon={informationCircleOutline} className="size-5 shrink-0 text-warning-120" />
          <p className="text-xs font-medium text-warning-120">
            {t("shift:hours.validation.breakUnusuallyLong")}
          </p>
        </div>
      )}
    </div>
  );
}
